import React, { useEffect, useState, useRef, createElement } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordResetModal } from '../PasswordResetModal';
import { AlertCircleIcon, CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
// ─── Cloudflare Turnstile config ────────────────────────────────────────
// Feature flag: flip to `false` to disable the CAPTCHA gate entirely
// (e.g. emergency rollback) without removing any code.
const ENABLE_LOGIN_CAPTCHA = true;
// Number of failed login attempts before Turnstile is required for further
// attempts in the same page session. 3 catches automation early while
// tolerating honest typos.
const LOGIN_CAPTCHA_THRESHOLD = 3;
// Cloudflare Turnstile PUBLIC site key. Safe to commit.
const TURNSTILE_SITE_KEY = '0x4AAAAAADYnh4MLjOOURJjN';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement | string, params: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}
export const StudentLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // When we detect that the visitor is already authenticated (e.g. they
  // arrived on this page via a stale verification tab after already signing
  // in elsewhere), we render a minimal "Already signed in" state and
  // redirect to the appropriate dashboard. This avoids showing a duplicate
  // sign-in form to a user who already has an active session.
  const [alreadySignedIn, setAlreadySignedIn] = useState(false);
  // ─── Failed-attempt tracking + Turnstile state ───────────────────────
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const hasRedirectedRef = useRef(false);
  // Tracks whether the user submitted the sign-in form on this page. When
  // true, the "already signed in" detection effect should bail out — the
  // active submit handler owns the post-login redirect, so we must not
  // briefly render the "Already signed in" panel during the auth-state
  // transition.
  const didSubmitRef = useRef(false);
  const {
    login,
    currentUser,
    userType
  } = useAuth();
  // Check for verification success or error from URL params
  const isVerified = searchParams.get('verified') === 'true';
  const verificationError = searchParams.get('error') === 'verification_failed';
  const captchaRequired = ENABLE_LOGIN_CAPTCHA && failedAttempts >= LOGIN_CAPTCHA_THRESHOLD;
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    localStorage.removeItem('scrollToTopAfterSignIn');
  }, []);
  // Detect already-authenticated visitors and route them to the correct
  // dashboard instead of showing a redundant sign-in form. This handles the
  // case where the verification tab auto-redirects here after another tab
  // has already completed sign-in.
  useEffect(() => {
    if (hasRedirectedRef.current) return;
    if (!currentUser) return;
    // Only redirect once we have a verified session — unverified users still
    // need to complete the verification flow before reaching the dashboard.
    if (!currentUser.emailVerified) return;
    // If the user is actively signing in via the form on this page, skip the
    // "Already signed in" UI entirely. handleSubmit owns the redirect and
    // showing the intermediate panel would cause a brief flash before the
    // dashboard mounts.
    if (didSubmitRef.current) return;
    hasRedirectedRef.current = true;
    setAlreadySignedIn(true);
    // Resolve destination from AuthContext userType (already resolved from
    // Firestore by AuthContext on session start). Fallback to localStorage
    // for parity with the rest of the verification flow. Default to the
    // student dashboard since this is the student login page.
    const resolvedType = userType || localStorage.getItem('userType') || 'student';
    const destination = resolvedType === 'teacher' ? '/teacher-dashboard' : '/student-dashboard';
    // Small delay so the success state renders cleanly before navigation.
    setTimeout(() => {
      navigate(destination, {
        replace: true
      });
    }, 600);
  }, [currentUser, userType, navigate]);
  // Scroll to error message when error state changes
  useEffect(() => {
    if (error) {
      window.scrollTo(0, 0);
      if (errorRef.current) {
        setTimeout(() => {
          errorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  }, [error]);
  // ─── Load Turnstile script once (idempotent across components) ──────
  useEffect(() => {
    if (!ENABLE_LOGIN_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    if (window.turnstile) return;
    if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)) return;
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);
  // ─── Render the Turnstile widget once the threshold is reached ──────
  useEffect(() => {
    if (!captchaRequired) return;
    if (typeof window === 'undefined') return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const tryRender = () => {
      if (cancelled) return false;
      const container = turnstileContainerRef.current;
      if (!container || !window.turnstile) return false;
      if (turnstileWidgetIdRef.current) return true;
      try {
        const widgetId = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            if (!cancelled) setTurnstileToken(token);
          },
          'expired-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          'error-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          theme: 'light'
        });
        turnstileWidgetIdRef.current = widgetId;
        return true;
      } catch (err) {
        console.error('[Turnstile] render failed:', err);
        return false;
      }
    };
    if (!tryRender()) {
      intervalId = setInterval(() => {
        if (tryRender() && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 200);
    }
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [captchaRequired]);
  const resetTurnstileWidget = () => {
    if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    }
    setTurnstileToken(null);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Mark the form as actively submitting so the already-signed-in
    // detection effect bails out while auth state transitions.
    didSubmitRef.current = true;
    // ─── Turnstile gate (only active once threshold is reached) ──────
    if (captchaRequired) {
      if (!turnstileToken) {
        setError('Please complete the verification challenge before signing in.');
        didSubmitRef.current = false;
        return;
      }
      setIsSubmitting(true);
      const honeypotEl = document.getElementById('mc-sl-website-hp') as HTMLInputElement | null;
      const honeypot = honeypotEl?.value ?? '';
      const verifyFn = httpsCallable<{
        token: string;
        honeypot: string;
      }, {
        verified: boolean;
        errors?: string[];
      }>(functions, 'verifyTurnstile');
      let verifyResult;
      try {
        verifyResult = await verifyFn({
          token: turnstileToken,
          honeypot
        });
      } catch (verifyErr) {
        console.error('[Turnstile] verification call failed:', verifyErr);
        setError('Verification could not be completed. Please refresh the challenge and try again.');
        resetTurnstileWidget();
        setIsSubmitting(false);
        didSubmitRef.current = false;
        return;
      }
      if (!verifyResult.data?.verified) {
        console.warn('[Turnstile] verification rejected:', verifyResult.data?.errors);
        setError('Verification failed. Please complete the challenge again and resubmit.');
        resetTurnstileWidget();
        setIsSubmitting(false);
        didSubmitRef.current = false;
        return;
      }
    } else {
      setIsSubmitting(true);
    }
    try {
      console.log('=== STUDENT LOGIN ATTEMPT ===');
      console.log('Email:', email);
      console.log('Calling login with type: student');
      // Use Firebase authentication
      await login(email, password, 'student');
      console.log('Login successful, navigating to dashboard');
      // Reset failure counter on success
      setFailedAttempts(0);
      // Navigate to dashboard on successful login
      navigate('/student-dashboard');
    } catch (error: any) {
      console.error('=== STUDENT LOGIN ERROR ===');
      console.error('Login error:', error);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      // Determine if this is a credential-class failure (attacker signal)
      // vs a transient/environmental failure (not an attacker signal).
      const isNetwork = error.code === 'auth/network-request-failed';
      const isFirebaseRateLimit = error.code === 'auth/too-many-requests';
      // Handle user-actionable errors specifically
      if (isFirebaseRateLimit) {
        setError('Too many failed login attempts. Please try again later or reset your password.');
      } else if (isNetwork) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        // Generic message for all auth failures (security best practice)
        setError('Invalid email or password');
        // Increment only on actual credential failures
        setFailedAttempts(n => n + 1);
      }
      // Always refresh the challenge after a failed attempt while the gate
      // is active, so a fresh token is required for the next retry.
      if (captchaRequired) {
        resetTurnstileWidget();
      }
      // Sign-in failed — clear the active-submit flag so a subsequent valid
      // session restore (e.g. via "Forgot password" flow) can still trigger
      // the already-signed-in detection.
      didSubmitRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };
  const submitDisabled = isSubmitting || captchaRequired && !turnstileToken;
  // Render a clean "Already signed in" state instead of the sign-in form
  // when we've detected an existing authenticated, verified session. The
  // redirect to the appropriate dashboard is already scheduled by the
  // detection effect above.
  if (alreadySignedIn) {
    return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-7233">
        <Navbar data-id="element-7234" />
        <main className="pt-32 pb-16" data-id="element-7235">
          <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7236">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-8 text-center" data-id="element-7237">
              <div className="bg-green-50 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center" data-id="element-7238">
                <CheckCircleIcon className="h-8 w-8 text-green-500" data-id="element-7239" />
              </div>
              <h2 className="text-2xl font-serif font-bold text-navy-900 mb-2" data-id="element-7240">
                You're already signed in
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-7241">
                Taking you to your dashboard…
              </p>
              <Link to={(userType || localStorage.getItem('userType')) === 'teacher' ? '/teacher-dashboard' : '/student-dashboard'} className="inline-block bg-deep-pink hover:bg-burgundy text-white font-medium py-3 px-6 rounded-lg transition-colors" data-id="element-7242">
                Continue to dashboard
              </Link>
            </div>
          </div>
        </main>
        <Footer data-id="element-7243" />
      </div>;
  }
  return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-7244">
      <Navbar data-id="element-7245" />
      <main className="pt-32 pb-16" data-id="element-7246">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7247">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 p-8" data-id="element-7248">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-1 text-center" data-id="element-7249">
              Student Login
            </h2>
            <p className="text-gray-600 text-center mb-6" data-id="element-7250">
              Sign in to access your student dashboard
            </p>

            {/* Email verified success banner */}
            {isVerified && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start mb-6" data-id="element-7251">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-7252" />
                <span data-id="element-7253">
                  Email verified successfully! Please sign in to continue.
                </span>
              </div>}

            {/* Verification error banner */}
            {verificationError && <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg flex items-start mb-6" data-id="element-7254">
                <AlertCircleIcon className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-7255" />
                <span data-id="element-7256">
                  Email verification failed. Please try again or request a new
                  verification email.
                </span>
              </div>}

            {/* Error message at the top of the form */}
            {error && <div ref={errorRef} className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start mb-6" data-id="element-7257">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-7258" />
                <span data-id="element-7259">{error}</span>
              </div>}
            <form ref={formRef} onSubmit={handleSubmit} data-id="element-7260">
              <div className="space-y-5" data-id="element-7261">
                {/* Email Address */}
                <div data-id="element-7262">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7263">
                    Email Address
                  </label>
                  <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="you@example.com" required autoCapitalize="off" autoComplete="email" autoCorrect="off" spellCheck="false" inputMode="email" data-id="element-7264" />
                </div>
                {/* Password */}
                <div data-id="element-7265">
                  <div className="flex items-center justify-between mb-1" data-id="element-7266">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-7267">
                      Password
                    </label>
                    <button type="button" onClick={() => setIsResetModalOpen(true)} className="text-xs text-deep-pink hover:text-burgundy" data-id="element-7268">
                      Forgot password?
                    </button>
                  </div>
                  <input type="password" id="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors text-gray-900" placeholder="Enter your password" required autoComplete="current-password" autoCapitalize="off" data-id="element-7269" />
                </div>

                {/* ─── Honeypot (visually hidden) ─── */}
                {captchaRequired && <div aria-hidden="true" style={{
                position: 'absolute',
                left: '-10000px',
                top: 'auto',
                width: '1px',
                height: '1px',
                overflow: 'hidden'
              }} data-id="element-7270">
                    <label htmlFor="mc-sl-website-hp" data-id="element-7271">
                      Website (leave blank)
                    </label>
                    <input id="mc-sl-website-hp" name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" data-id="element-7272" />
                  </div>}

                {/* ─── Cloudflare Turnstile widget (only after threshold) ─── */}
                {captchaRequired && <div data-id="element-7273">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7274">
                      Human Verification
                    </label>
                    <div className="flex justify-center" data-id="element-7275">
                      <div ref={turnstileContainerRef} data-id="element-7276" />
                    </div>
                    <p className="mt-2 text-xs text-gray-500 text-center" data-id="element-7277">
                      Additional verification is required after multiple failed
                      sign-in attempts.
                    </p>
                  </div>}

                {/* Submit Button */}
                <button type="submit" disabled={submitDisabled} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-7278">
                  {isSubmitting ? <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-7279">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-7280"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-7281"></path>
                      </svg>
                      Signing In...
                    </> : 'Sign In'}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center pt-4 border-t border-gray-100" data-id="element-7282">
              <p className="text-sm text-gray-600" data-id="element-7283">
                Don't have an account?{' '}
                <Link to="/student-signup" className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-7284">
                  Create account
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-7285" />
      {/* Password Reset Modal */}
      <PasswordResetModal isOpen={isResetModalOpen} onClose={() => setIsResetModalOpen(false)} data-id="element-7286" />
    </div>;
};